import { playExitMatrix } from '@/lib/audio/matrixSfx';
import { useBitcoinData } from '@/hooks/useBitcoinData';
import { useBuddyBackend } from '@/hooks/useBuddyBackend';
import { useBuddyVisuals } from '@/hooks/useBuddyVisuals';
import { useTransientReactions } from '@/hooks/useTransientReactions';
import BuddyCharacter from '@/components/BuddyCharacter';
import SceneBackground from '@/components/SceneBackground';
import IslandBarometer from '@/components/IslandBarometer';
import EmotionalLighting, { useBuddyMacroFilter } from '@/components/EmotionalLighting';
import type { MacroLightState } from '@/lib/lighting/emotionalLightingEngine';
import { deriveSceneLighting } from '@/lib/lighting/emotionalLightingEngine';

import { deriveMacroFromBackendState } from '@/lib/state/macroAdvisorEngine';
import { resolveDisplayMacroState, MACRO_DISPLAY_COPY, selectDailyFooterLine, type MacroDisplayState } from '@/config/macroDisplayCopy';
import type { MacroDisplayBundle } from '@/lib/types/macroDisplay';

import { motion, AnimatePresence } from 'framer-motion';
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';

import { useOnboarding } from '@/components/OnboardingSheet';
import { useStackSatsGame } from '@/hooks/useStackSatsGame';

import { toast } from 'sonner';
import { lazy, Suspense } from 'react';

// Layout components
import HudHeader from '@/components/layout/HudHeader';
import AppFooter from '@/components/layout/AppFooter';
import DebugPanel from '@/components/layout/DebugPanel';
import MatrixHeader from '@/components/layout/MatrixHeader';

const ShareModal = lazy(() => import('@/components/ShareModal'));
const OnboardingSheet = lazy(() => import('@/components/OnboardingSheet'));
const BuddyGuide = lazy(() => import('@/components/BuddyGuide'));
const MatrixRain = lazy(() => import('@/components/MatrixRain'));
const MatrixSplash = lazy(() => import('@/components/MatrixSplash'));
const StackSatsGame = lazy(() => import('@/components/StackSatsGame'));
const DonationSheet = lazy(() => import('@/components/DonationSheet'));
const PwaInstallBanner = lazy(() => import('@/components/PwaInstallBanner'));

const Index = () => {
  const backend = useBuddyBackend();
  const { network, blockJustFound: realBlockJustFound, loading, error } = useBitcoinData();
  const transientManager = useTransientReactions();
  const [simulateBlock, setSimulateBlock] = useState(false);
  const blockJustFound = realBlockJustFound || simulateBlock;

  // Auto-clear simulated block
  useEffect(() => {
    if (simulateBlock) {
      const t = setTimeout(() => setSimulateBlock(false), 4000);
      return () => clearTimeout(t);
    }
  }, [simulateBlock]);

  // Trigger transient on block found
  const prevBlockHeight = useRef(0);
  useEffect(() => {
    if (realBlockJustFound && network.lastBlockHeight > 0 && network.lastBlockHeight !== prevBlockHeight.current) {
      prevBlockHeight.current = network.lastBlockHeight;
      transientManager.triggerTransient('blockFound');
    }
  }, [realBlockJustFound, network.lastBlockHeight]);

  // Also trigger transient on simulated block
  useEffect(() => {
    if (simulateBlock) {
      transientManager.triggerTransient('blockFound');
    }
  }, [simulateBlock]);

  // ── Transient triggers ──

  const prevSentimentRef = useRef<string | null>(null);
  useEffect(() => {
    const bucket = backend.state?.sentimentBucket ?? null;
    if (bucket && prevSentimentRef.current && bucket !== prevSentimentRef.current) {
      if (bucket === 'extreme_fear') {
        transientManager.triggerTransient('fearSurge');
      } else if (bucket === 'extreme_greed') {
        transientManager.triggerTransient('greedPeak');
      }
    }
    prevSentimentRef.current = bucket;
  }, [backend.state?.sentimentBucket]);

  const prevMedFeeRef = useRef(0);
  useEffect(() => {
    const currentFee = network.recommendedFees.medium;
    if (prevMedFeeRef.current > 0 && currentFee > 0) {
      const dropPct = (prevMedFeeRef.current - currentFee) / prevMedFeeRef.current;
      if (dropPct > 0.3) {
        transientManager.triggerTransient('feeRelief');
      }
    }
    prevMedFeeRef.current = Math.max(prevMedFeeRef.current, currentFee);
  }, [network.recommendedFees.medium]);

  const droughtFiredRef = useRef(false);
  useEffect(() => {
    const timeSince = network.timeSinceLastBlock ?? 0;
    if (timeSince > 1080 && !droughtFiredRef.current) {
      droughtFiredRef.current = true;
      transientManager.triggerTransient('droughtAnxiety');
    }
    if (timeSince < 120) {
      droughtFiredRef.current = false;
    }
  }, [network.timeSinceLastBlock]);

  const { composed, environment, heatLevel, activeSignature } = useBuddyVisuals(
    backend.state,
    transientManager.activeTransient,
  );

  const [showDebug, setShowDebug] = useState(false);
  const toggleDebug = useCallback(() => setShowDebug(d => !d), []);
  const [showInfo, setShowInfo] = useState(false);
  const onboarding = useOnboarding();
  const [hudExpanded, setHudExpanded] = useState(false);
  const [showBarometer, setShowBarometer] = useState(false);
  const [shareCapture, setShareCapture] = useState<string | null>(null);
  const [shareLoading, setShareLoading] = useState(false);

  const [showGuide, setShowGuide] = useState(false);
  const [matrixMode, setMatrixMode] = useState(false);
  const [matrixSplash, setMatrixSplash] = useState(false);
  const [redFlash, setRedFlash] = useState(false);
  const [blueFlash, setBlueFlash] = useState(false);
  const [stackSatsLoading, setStackSatsLoading] = useState(false);
  const [showDonation, setShowDonation] = useState(false);
  const [worldDimming, setWorldDimming] = useState(false);

  // Stack Sats game
  const stackSats = useStackSatsGame(
    network.mempoolCongestion,
    blockJustFound,
    network.lastBlockHeight,
  );


  const sceneRef = useRef<HTMLDivElement>(null);
  const buddyCaptureRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState(() => ({
    width: window.innerWidth,
    height: window.innerHeight,
  }));

  useEffect(() => {
    const handleResize = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const buddyScale = viewport.width <= 768
    ? Math.min(1.5, Math.max(1.22, viewport.width / 280))
    : Math.min(1.6, Math.max(1.05, Math.min(viewport.width / 340, viewport.height / 540 + 0.35)));

  const bs = backend.state;
  const hasBackend = !!bs && !backend.loading;

  const displayEvents = hasBackend && backend.events.length > 0
    ? backend.events.slice(0, 3)
    : [];

  const displayVerdict = bs?.verdict ?? 'Observing conditions.';
  const displayArchetype = bs?.dayArchetype ?? 'Calm Chain Day';
  const displayWeather = bs?.weather ?? 'clear';

  // ── Macro display bundle (single source of truth) ──
  const macroBundle: MacroDisplayBundle = useMemo(() => {
    // 1. Resolve macro state key
    const state: MacroDisplayState = (bs?.displayMacroState && bs.displayMacroState in MACRO_DISPLAY_COPY)
      ? bs.displayMacroState as MacroDisplayState
      : resolveDisplayMacroState(bs?.macroAdvisor?.macroLabel, bs?.macroMode);

    // 2. Copy layers (backend overrides → fallback to static copy)
    const copy = MACRO_DISPLAY_COPY[state];
    const moodLabel = bs?.moodLabel || copy.moodLabel;
    const plainSubtitle = bs?.plainSubtitle || copy.plainSubtitle;
    const macroExplanation = bs?.macroExplanation || copy.explanation;
    const whatItMeans = bs?.whatItMeans || copy.whatItMeans;
    const footerLine = bs?.footerLine || selectDailyFooterLine(copy.footerLines);

    // 3. Advisory (full scoring)
    const congestion = bs?.heatLevel ?? 0;
    const advisory = bs?.macroAdvisor
      ? bs.macroAdvisor
      : deriveMacroFromBackendState(
          bs?.macroMode ?? 'NEAR_200WMA',
          bs?.seasonState ?? 'neutral',
          congestion,
          bs?.ltConfidence ?? 'low',
        );

    return { state, moodLabel, plainSubtitle, macroExplanation, whatItMeans, footerLine, advisory };
  }, [
    bs?.displayMacroState, bs?.macroAdvisor?.macroLabel, bs?.macroMode,
    bs?.moodLabel, bs?.plainSubtitle, bs?.macroExplanation, bs?.whatItMeans, bs?.footerLine,
    bs?.macroAdvisor, bs?.seasonState, bs?.heatLevel, bs?.ltConfidence,
  ]);

  // Convenience aliases used by non-macro components
  const displayMacroState = macroBundle.state;

  // ── Matrix mode handlers ──
  const handleMatrixEnter = useCallback(() => {
    setWorldDimming(true);
    setTimeout(() => {
      setRedFlash(true);
      const root = sceneRef.current;
      if (root) {
        root.style.animation = 'exit-shake 1200ms ease-out';
        root.addEventListener('animationend', () => { root.style.animation = ''; }, { once: true });
      }
      setTimeout(() => { setRedFlash(false); }, 800);
      setTimeout(() => { setMatrixSplash(true); }, 1200);
    }, 150);
  }, []);

  const handleMatrixExit = useCallback(() => {
    playExitMatrix();
    setBlueFlash(true);
    const root = document.getElementById('root');
    if (root) {
      root.style.animation = 'exit-shake 1200ms ease-out';
      root.addEventListener('animationend', () => { root.style.animation = ''; }, { once: true });
    }
    setTimeout(() => { setBlueFlash(false); }, 800);
    setTimeout(() => { setMatrixMode(false); }, 1200);
  }, []);

  // ── Loading ──
  if (backend.loading && network.btcPrice === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center noise-overlay">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="text-center space-y-4"
        >
          <motion.div
            className="text-4xl"
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          >
            ⛏️
          </motion.div>
          <p className="font-pixel text-[10px] text-bitcoin text-glow tracking-wider">
            Mempool Buddy
          </p>
          <p className="text-[10px] text-muted-foreground/60 font-body font-light">
            Connecting to the Bitcoin network...
          </p>
          <motion.div
            className="mx-auto h-px w-24 rounded-full overflow-hidden"
            style={{ background: 'hsl(var(--border))' }}
          >
            <motion.div
              className="h-full w-12 rounded-full"
              style={{ background: 'hsl(var(--bitcoin) / 0.5)' }}
              animate={{ x: ['-100%', '200%'] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
            />
          </motion.div>
        </motion.div>
      </div>
    );
  }

  // ── Error ──
  if (backend.error && error && network.btcPrice === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center noise-overlay">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center space-y-3 px-8"
        >
          <div className="text-3xl">😵‍💫</div>
          <p className="font-pixel text-[9px] text-bitcoin/70 tracking-wider">
            Connection lost
          </p>
          <p className="text-[10px] text-muted-foreground/50 font-body font-light max-w-[200px]">
            Couldn't reach the network. Buddy will reconnect automatically.
          </p>
        </motion.div>
      </div>
    );
  }

  const networkState = (bs?.networkState ?? 'quiet') as any;
  const blockState = (bs?.blockState ?? 'normal') as any;
  const priceState = (bs?.priceState ?? 'flat') as any;
  const feeHeat = Math.min(1, network.recommendedFees.medium / 80);

  return (
    <Suspense fallback={null}>
    <div ref={sceneRef} className={`min-h-screen relative flex flex-col items-center overflow-hidden ${redFlash ? 'screen-shake-heavy' : blueFlash ? 'screen-shake' : ''}`} style={{ background: matrixMode ? '#000' : 'hsl(var(--background))' }}>
      {/* Matrix splash overlay */}
      <AnimatePresence>
        {matrixSplash && (
          <MatrixSplash
            key="matrix-splash"
            onTransition={() => {}}
            onReveal={() => setMatrixMode(true)}
            onComplete={() => { setMatrixSplash(false); setWorldDimming(false); }}
          />
        )}
      </AnimatePresence>

      {/* Red/Blue flash overlays */}
      <AnimatePresence>
        {redFlash && (
          <>
            <motion.div key="red-core" className="fixed inset-0 z-[9998] pointer-events-none"
              style={{ background: 'radial-gradient(ellipse 50% 45% at 50% 55%, rgba(255,30,30,0.6) 0%, rgba(200,20,20,0.25) 40%, transparent 70%)' }}
              initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1.1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }} />
            <motion.div key="red-bleed" className="fixed inset-0 z-[9998] pointer-events-none"
              style={{ background: 'radial-gradient(ellipse 90% 70% at 48% 52%, rgba(180,20,20,0.35) 0%, rgba(120,10,10,0.15) 50%, rgba(60,0,0,0.06) 80%, transparent 100%)' }}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: 'easeOut' }} />
            <motion.div key="red-vignette" className="fixed inset-0 z-[9998] pointer-events-none"
              style={{ background: 'radial-gradient(ellipse 120% 100% at 50% 50%, transparent 30%, rgba(100,0,0,0.2) 80%, rgba(40,0,0,0.35) 100%)' }}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut' }} />
          </>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {blueFlash && (
          <motion.div key="blue-flash" className="fixed inset-0 z-[9998] pointer-events-none"
            style={{ background: 'radial-gradient(circle at center, rgba(80,150,255,0.5) 0%, rgba(60,130,246,0.3) 50%, rgba(20,60,140,0.18) 100%)' }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }} />
        )}
      </AnimatePresence>

      {/* Scene backgrounds */}
      <AnimatePresence>
        {!matrixMode && (
          <motion.div key="island-bg" initial={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }} className="absolute inset-0 z-0">
            <SceneBackground weather={displayWeather} seasonState={bs?.seasonState} seasonIntensity={bs?.seasonIntensity} heatLevel={heatLevel} minerActivity={environment?.minerActivity ?? 0.5} />
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {matrixMode && (
          <MatrixRain
            active={matrixMode}
            congestion={network.mempoolCongestion}
            pendingTxCount={network.pendingTxCount}
            feeFast={network.recommendedFees.high}
            feeMedium={network.recommendedFees.medium}
            feeSlow={network.recommendedFees.low}
            blockHeight={network.lastBlockHeight}
            blockJustFound={blockJustFound}
            mempoolVsize={network.mempoolVsize}
            hashrateTHs={network.hashrateTHs}
          />
        )}
      </AnimatePresence>

      {/* Stack Sats minigame overlay */}
      {matrixMode && stackSats.phase !== 'idle' && (
        <StackSatsGame
          phase={stackSats.phase}
          score={stackSats.score}
          capturedPct={stackSats.capturedPct}
          elapsed={stackSats.elapsed}
          maxRoundSec={stackSats.maxRoundSec}
          result={stackSats.result}
          captures={stackSats.captures}
          blockFoundBanner={stackSats.blockFoundBanner}
          congestionTier={stackSats.congestionTier}
          usedBlockSpace={stackSats.usedBlockSpace}
          blockCapacity={stackSats.blockCapacity}
          tick={stackSats.tick}
          updateBuddyPos={stackSats.updateBuddyPos}
          onDismiss={stackSats.dismiss}
          onReplay={stackSats.startGame}
          onLaunchRound={stackSats.launchRound}
          onLockBlock={stackSats.lockBlock}
          onExit={stackSats.endGame}
          onCompleteMining={stackSats.completeMining}
        />
      )}

      <EmotionalLighting
        macro={macroBundle}
        mempoolPressure={heatLevel}
        blockJustFound={blockJustFound}
        heatLevel={heatLevel}
      />

      {/* Soft atmospheric gradient overlay behind top text */}
      {!matrixMode && (
        <div
          data-share-exclude="true"
          className="absolute inset-x-0 top-0 z-[5] pointer-events-none"
          style={{
            height: '45%',
            background: 'linear-gradient(180deg, hsl(var(--background) / 0.65) 0%, hsl(var(--background) / 0.35) 40%, hsl(var(--background) / 0.08) 70%, transparent 100%)',
          }}
        />
      )}

      {/* ── Header (normal mode) ── */}
      <AnimatePresence>
        {stackSats.phase === 'idle' && !matrixMode && (
          <HudHeader
            network={network}
            bs={bs}
            hasBackend={hasBackend}
            blockJustFound={blockJustFound}
            hudExpanded={hudExpanded}
            setHudExpanded={setHudExpanded}
            worldDimming={worldDimming}
            activeSignature={activeSignature}
            displayVerdict={displayVerdict}
            macro={macroBundle}
            activeTransient={transientManager.activeTransient}
            onShowGuide={() => setShowGuide(true)}
            onToggleDebug={toggleDebug}
          />
        )}
      </AnimatePresence>

      {/* ── Header (matrix mode) ── */}
      <AnimatePresence>
        {matrixMode && stackSats.phase === 'idle' && (
          <MatrixHeader network={network} />
        )}
      </AnimatePresence>

      {/* Buddy — absolutely positioned */}
      <AnimatePresence>
      {stackSats.phase === 'idle' && (
      <motion.div
        className="absolute inset-0 z-20 pointer-events-none"
        initial={false}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, transition: { duration: 0.3 } }}
      >
      <div
        ref={buddyCaptureRef}
        style={{
          transform: `translateX(-50%) scale(${buddyScale})`,
          transformOrigin: 'center bottom',
          top: viewport.width <= 768
            ? `${Math.min(34, 26 + Math.max(0, (viewport.height / Math.max(viewport.width, 1) - 1.75) * 8)).toFixed(2)}%`
            : '24%',
          position: 'absolute',
          left: '50%',
          filter: matrixMode ? 'none' : deriveSceneLighting({ macroState: (displayMacroState || 'NEUTRAL') as MacroLightState, mempoolPressure: 0, blockJustFound: false, heatLevel: 0 }).buddyFilter,
          transition: 'filter 2s ease',
        }}
      >
        <div
          className="relative flex flex-col items-center cursor-pointer pointer-events-auto"
          onClick={() => { if (!matrixMode) setShowBarometer(true); }}
        >
          <AnimatePresence>
            {blockJustFound && (
              <motion.div
                className="absolute inset-0 z-0 rounded-full"
                initial={{ opacity: 0, scale: 0.7 }}
                animate={{ opacity: 0.55, scale: 1.35 }}
                exit={{ opacity: 0, scale: 1.5 }}
                transition={{ duration: 1.2, ease: 'easeOut' }}
                style={{
                  background: 'radial-gradient(circle, hsla(35, 75%, 60%, 0.35) 0%, hsla(35, 70%, 55%, 0.12) 45%, transparent 70%)',
                  filter: 'blur(8px)',
                }}
              />
            )}
          </AnimatePresence>
          <BuddyCharacter
            composed={composed}
            activeSignature={activeSignature}
            heatLevel={heatLevel}
            blockRelief={blockJustFound}
            seasonState={matrixMode ? undefined : bs?.seasonState}
            weather={matrixMode ? undefined : displayWeather}
            lightningVitality={environment?.lightningVitality ?? 0.5}
            displayMacroState={bs?.displayMacroState ?? displayMacroState}
            matrixMode={matrixMode}
            enteringMatrix={redFlash}
            exitingMatrix={blueFlash}
          />
        </div>
      </div>
      </motion.div>
      )}
      </AnimatePresence>

      {/* Barometer */}
      <IslandBarometer
        macro={macroBundle}
        expanded={showBarometer}
        onClose={() => setShowBarometer(false)}
        networkState={bs?.networkState}
        blockState={bs?.blockState}
        activeSignature={activeSignature}
        heatLevel={heatLevel}
        recentEventHeadline={displayEvents[0]?.headline ?? null}
        ltConfidence={bs?.ltConfidence}
      />

      {/* Spacer */}
      <div className="flex-1" />

      {/* Debug panel */}
      <AnimatePresence>
        {showDebug && hasBackend && (
          <DebugPanel
            bs={bs!}
            meta={backend.meta}
            macro={macroBundle}
            activeTransient={transientManager.activeTransient}
            transientDebug={transientManager.debug}
            onClose={toggleDebug}
            onSimulateBlock={() => setSimulateBlock(true)}
          />
        )}
      </AnimatePresence>

      {/* ── Footer ── */}
      <AnimatePresence>
      {stackSats.phase === 'idle' && (
        <AppFooter
          network={network}
          matrixMode={matrixMode}
          newsItems={bs?.newsItems}
          macro={macroBundle}
          stackSatsPhase={stackSats.phase}
          stackSatsLoading={stackSatsLoading}
          worldDimming={worldDimming}
          sceneRef={sceneRef}
          onMatrixEnter={handleMatrixEnter}
          onMatrixExit={handleMatrixExit}
          onStackSatsStart={stackSats.startGame}
          onSetStackSatsLoading={setStackSatsLoading}
          onShowDonation={() => setShowDonation(true)}
          onRefreshNews={backend.refreshNews}
          thoughtTickerProps={{
            mood: bs?.buddyMood ?? 'calm',
            weather: bs?.weather ?? 'clear',
            networkState: bs?.networkState ?? 'quiet',
            blockState: bs?.blockState ?? 'normal',
            priceState: bs?.priceState ?? 'flat',
            verdict: displayVerdict,
            sentimentBucket: bs?.sentimentBucket,
            activeSignature,
            blocksToHalving: network.blocksToHalving,
            hashrateChange7d: network.hashrateChange7d,
            lightningVitalityScore: Math.min(1, (network.lightningCapacitySats ?? 0) / (5000 * 1e8)),
            footerLine: macroBundle.footerLine,
            macroState: macroBundle.state,
            seasonState: bs?.seasonState,
          }}
        />
      )}
      </AnimatePresence>

      {/* Share Modal */}
      {shareCapture && (
        <ShareModal
          open={!!shareCapture}
          onClose={() => setShareCapture(null)}
          imageDataUrl={shareCapture}
          btcPrice={network.btcPrice}
          mediumFee={network.recommendedFees.medium}
          blockHeight={network.lastBlockHeight}
          archetype={displayArchetype}
          verdict={displayVerdict}
          dailySummary={bs?.dailySummary}
          weeklyVibe={bs?.weeklyVibe}
          macro={macroBundle}
        />
      )}

      {/* Donation Sheet */}
      <DonationSheet open={showDonation} onClose={() => setShowDonation(false)} />

      <BuddyGuide
        open={showGuide}
        onClose={() => setShowGuide(false)}
        bs={bs}
        network={network}
        events={backend.events}
        meta={backend.meta}
        activeTransient={transientManager.activeTransient}
        transientDebug={transientManager.debug}
        weeklyVibe={bs?.weeklyVibe}
        dailySummary={bs?.dailySummary}
        onResetOnboarding={() => {
          setShowGuide(false);
          onboarding.reset();
        }}
      />

      {/* Onboarding */}
      <OnboardingSheet show={onboarding.needsOnboarding} onComplete={onboarding.complete} />

      {/* PWA Install Banner */}
      <PwaInstallBanner />
    </div>
    </Suspense>
  );
};

export default Index;
